/**
 * Stima deterministica degli spostamenti.
 *
 * E' l'implementazione di default di `RoutingProvider`: nessun servizio esterno,
 * nessun costo, risultato riproducibile. I tempi prodotti sono SEMPRE marcati
 * `estimated` e l'interfaccia lo mostra — non si spaccia una stima per un
 * percorso calcolato su grafo stradale.
 *
 * Con Valhalla self-hosted configurato, il planner riceve gli stessi tipi con
 * `status: 'verified'` e non cambia una riga.
 */

import type { DataStatus, TransportMode } from '../domain.js';

export interface LatLng {
  readonly lat: number;
  readonly lon: number;
}

export interface RouteEstimate {
  readonly durationSec: number;
  readonly distanceM: number;
  readonly mode: TransportMode;
  readonly status: DataStatus;
  readonly source: string;
}

const EARTH_RADIUS_M = 6_371_000;

export function haversineDistanceM(a: LatLng, b: LatLng): number {
  const toRad = (deg: number): number => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Parametri per profilo.
 *
 * `detour` traduce la distanza in linea d'aria in distanza reale su strada;
 * `speedKmh` e' la velocita' media urbana porta-a-porta (non di punta);
 * `fixedSec` copre i tempi che non dipendono dalla distanza — trovare la fermata,
 * aspettare, parcheggiare. Sono i motivi per cui "5 minuti a piedi" in linea
 * d'aria non sono mai 5 minuti reali.
 */
interface ModeProfile {
  readonly detour: number;
  readonly speedKmh: number;
  readonly fixedSec: number;
  readonly minSec: number;
}

const PROFILES: Record<TransportMode, ModeProfile> = {
  walk: { detour: 1.3, speedKmh: 4.5, fixedSec: 60, minSec: 120 },
  bike: { detour: 1.25, speedKmh: 14, fixedSec: 180, minSec: 180 },
  car: { detour: 1.4, speedKmh: 25, fixedSec: 420, minSec: 300 },
  // Trasporto pubblico senza feed GTFS: stima grossolana, dichiarata come tale.
  transit: { detour: 1.45, speedKmh: 18, fixedSec: 600, minSec: 420 },
  taxi: { detour: 1.4, speedKmh: 25, fixedSec: 300, minSec: 240 },
};

export function estimateRoute(from: LatLng, to: LatLng, mode: TransportMode): RouteEstimate {
  const profile = PROFILES[mode];
  const straight = haversineDistanceM(from, to);
  const distanceM = Math.round(straight * profile.detour);
  const travelSec = (distanceM / 1000 / profile.speedKmh) * 3600;
  const durationSec = Math.max(profile.minSec, Math.round(travelSec + profile.fixedSec));
  return { durationSec, distanceM, mode, status: 'estimated', source: 'estimate' };
}

/**
 * Sceglie il mezzo piu' sensato fra quelli che l'utente ha dichiarato di usare.
 *
 * A piedi sotto ~1,7 km (circa 25 minuti): oltre, camminare fra una tappa e
 * l'altra logora la giornata. Sopra i 12 km si preferisce l'auto/taxi se
 * disponibili.
 */
export function pickMode(
  from: LatLng,
  to: LatLng,
  allowed: readonly TransportMode[],
): TransportMode {
  const available = allowed.length > 0 ? allowed : (['walk'] as const);
  const straight = haversineDistanceM(from, to);

  const prefer = (...candidates: TransportMode[]): TransportMode | null =>
    candidates.find((mode) => available.includes(mode)) ?? null;

  if (straight <= 1700) return prefer('walk', 'bike', 'transit', 'car', 'taxi') ?? 'walk';
  if (straight <= 12_000) return prefer('transit', 'bike', 'car', 'taxi', 'walk') ?? 'walk';
  return prefer('car', 'taxi', 'transit', 'bike', 'walk') ?? 'walk';
}

export function estimateBestRoute(
  from: LatLng,
  to: LatLng,
  allowed: readonly TransportMode[],
): RouteEstimate {
  return estimateRoute(from, to, pickMode(from, to, allowed));
}

/** Matrice dei tempi: e' l'unica cosa che l'ottimizzatore usa dal routing. */
export function buildMatrix(
  points: readonly LatLng[],
  allowed: readonly TransportMode[],
): number[][] {
  return points.map((origin) =>
    points.map((destination) =>
      origin === destination ? 0 : estimateBestRoute(origin, destination, allowed).durationSec,
    ),
  );
}

export function formatDuration(seconds: number): string {
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) return `${String(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${String(hours)} h` : `${String(hours)} h ${String(rest)} min`;
}

export function formatDistance(meters: number): string {
  if (meters < 1000) return `${String(Math.round(meters / 10) * 10)} m`;
  return `${(meters / 1000).toFixed(1).replace('.', ',')} km`;
}
